import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
  </svg>
);

interface MenuItem {
  name: string;
  to: string;
  type: 'route' | 'anchor';
}

const menuItems: MenuItem[] = [
  { name: 'Accueil', to: '/', type: 'route' },
  { name: 'Services', to: '/#services', type: 'anchor' },
  { name: 'Projets', to: '/projets', type: 'route' },
  { name: 'À propos', to: '/#about', type: 'anchor' },
  { name: 'Contact', to: '/#contact', type: 'anchor' },
];

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isDark, toggle } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const headerBg = isDark
    ? scrolled ? 'rgba(10,25,41,.98)' : 'rgba(10,25,41,.92)'
    : scrolled ? 'rgba(255,255,255,.98)' : 'rgba(255,255,255,.92)';

  const borderColor = isDark
    ? scrolled ? 'rgba(74,170,212,.14)' : 'transparent'
    : scrolled ? 'var(--border)' : 'transparent';

  const shadow = scrolled
    ? isDark ? '0 2px 24px rgba(0,0,0,.3)' : '0 2px 24px rgba(26,111,168,.08)'
    : 'none';

  const isActiveRoute = (to: string) => {
    if (to === '/') return location.pathname === '/' && !location.hash;
    if (to === '/projets') return location.pathname === '/projets';
    return false;
  };

  const linkStyle = (active: boolean): React.CSSProperties => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '.63rem',
    fontWeight: 600,
    letterSpacing: '.2em',
    textTransform: 'uppercase',
    color: active ? 'var(--blue)' : 'var(--ink)',
    textDecoration: 'none',
    transition: 'color .2s ease',
    borderBottom: active ? '2px solid var(--gold)' : '2px solid transparent',
    paddingBottom: '4px',
  });

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        background: headerBg,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${borderColor}`,
        boxShadow: shadow,
        transition: 'background .3s ease, box-shadow .3s ease, border-color .3s ease',
      }}
    >
      <nav
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '0 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '68px',
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: '1.7rem',
            fontWeight: 600,
            color: 'var(--blue)',
            textDecoration: 'none',
            letterSpacing: '.04em',
            lineHeight: 1,
          }}
        >
          MIID
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex" style={{ alignItems: 'center', gap: '2.2rem' }}>
          {menuItems.slice(0, -1).map((item) => {
            const active = isActiveRoute(item.to);
            return (
              <Link
                key={item.name}
                to={item.to}
                style={linkStyle(active)}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--blue)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = active ? 'var(--blue)' : 'var(--ink)'; }}
              >
                {item.name}
              </Link>
            );
          })}

          {/* Theme toggle */}
          <button
            onClick={toggle}
            aria-label={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
            style={{
              width: '34px',
              height: '34px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: '1.5px solid var(--border)',
              cursor: 'pointer',
              color: 'var(--ink)',
              transition: 'border-color .2s, color .2s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--blue)';
              (e.currentTarget as HTMLElement).style.color = 'var(--blue)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
              (e.currentTarget as HTMLElement).style.color = 'var(--ink)';
            }}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          <Link to="/#contact" className="btn-primary" style={{ padding: '.65rem 1.6rem' }}>
            Contact
          </Link>
        </div>

        {/* Mobile right: toggle + hamburger */}
        <div className="flex md:hidden" style={{ alignItems: 'center', gap: '.75rem' }}>
          <button
            onClick={toggle}
            aria-label={isDark ? 'Mode clair' : 'Mode sombre'}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: '1.5px solid var(--border)',
              cursor: 'pointer',
              color: 'var(--ink)',
            }}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', display: 'flex', flexDirection: 'column', gap: '5px' }}
            aria-label="Menu"
          >
            <span style={{ display: 'block', width: '22px', height: '2px', background: 'var(--ink)', transition: 'all .25s ease', transform: isMenuOpen ? 'translateY(7px) rotate(45deg)' : 'none' }} />
            <span style={{ display: 'block', width: '22px', height: '2px', background: 'var(--ink)', opacity: isMenuOpen ? 0 : 1, transition: 'opacity .25s ease' }} />
            <span style={{ display: 'block', width: '22px', height: '2px', background: 'var(--ink)', transition: 'all .25s ease', transform: isMenuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none' }} />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        style={{
          overflow: 'hidden',
          maxHeight: isMenuOpen ? '380px' : '0',
          transition: 'max-height .35s ease',
          background: isDark ? 'rgba(10,25,41,.98)' : 'rgba(255,255,255,.98)',
          borderTop: isMenuOpen ? `1px solid var(--border)` : 'none',
        }}
      >
        <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          {menuItems.map((item) => {
            const active = isActiveRoute(item.to);
            return (
              <Link
                key={item.name}
                to={item.to}
                onClick={() => setIsMenuOpen(false)}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '.63rem',
                  fontWeight: 600,
                  letterSpacing: '.2em',
                  textTransform: 'uppercase',
                  color: active ? 'var(--blue)' : 'var(--ink)',
                  textDecoration: 'none',
                }}
              >
                {item.name}
              </Link>
            );
          })}
        </div>
      </div>
    </header>
  );
};

export default Header;
